"use client"

import React, { createContext, useContext, useState, useCallback } from "react"
import { motion, AnimatePresence } from "motion/react"
import { Icon } from "./Icon"

interface AccordionContextValue {
	openItems: Set<string>
	toggle: (value: string) => void
}

const AccordionContext = createContext<AccordionContextValue | undefined>(
	undefined,
)

interface AccordionItemContextValue {
	value: string
}

const AccordionItemContext = createContext<
	AccordionItemContextValue | undefined
>(undefined)

interface AccordionProps {
	type?: "single" | "multiple"
	defaultValue?: string | string[]
	children: React.ReactNode
	className?: string
}

export function Accordion({
	type = "single",
	defaultValue = [],
	children,
	className = "",
}: AccordionProps) {
	const [openItems, setOpenItems] = useState<Set<string>>(
		new Set(Array.isArray(defaultValue) ? defaultValue : [defaultValue]),
	)

	const toggle = useCallback(
		(value: string) => {
			setOpenItems((prev) => {
				const next = new Set(prev)
				if (next.has(value)) {
					next.delete(value)
				} else {
					if (type === "single") {
						next.clear()
					}
					next.add(value)
				}
				return next
			})
		},
		[type],
	)

	return (
		<AccordionContext.Provider value={{ openItems, toggle }}>
			<div className={className}>{children}</div>
		</AccordionContext.Provider>
	)
}

interface AccordionItemProps {
	value: string
	children: React.ReactNode
	className?: string
}

export function AccordionItem({
	value,
	children,
	className = "",
}: AccordionItemProps) {
	return (
		<AccordionItemContext.Provider value={{ value }}>
			<div
				className={`border-b border-neutral-200 dark:border-neutral-800 ${className}`}
				data-accordion-value={value}
			>
				{children}
			</div>
		</AccordionItemContext.Provider>
	)
}

interface AccordionTriggerProps {
	children: React.ReactNode
	className?: string
}

export function AccordionTrigger({
	children,
	className = "",
}: AccordionTriggerProps) {
	const context = useContext(AccordionContext)
	const itemContext = useContext(AccordionItemContext)
	const value = itemContext?.value ?? ""
	const isOpen = context?.openItems.has(value) ?? false

	return (
		<button
			type="button"
			onClick={() => context?.toggle(value)}
			className={`flex w-full items-center justify-between py-4 text-left font-medium text-neutral-900 transition-colors hover:text-primary dark:text-white dark:hover:text-primary ${className}`}
		>
			<span>{children}</span>
			<motion.span
				animate={{ rotate: isOpen ? 90 : 0 }}
				transition={{ duration: 0.2 }}
				className="inline-flex"
			>
				<Icon name="chevron_right" size={20} />
			</motion.span>
		</button>
	)
}

interface AccordionContentProps {
	children: React.ReactNode
	className?: string
}

export function AccordionContent({
	children,
	className = "",
}: AccordionContentProps) {
	const context = useContext(AccordionContext)
	const itemContext = useContext(AccordionItemContext)
	const value = itemContext?.value ?? ""
	const isOpen = context?.openItems.has(value) ?? false

	return (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					initial={{ height: 0, opacity: 0 }}
					animate={{ height: "auto", opacity: 1 }}
					exit={{ height: 0, opacity: 0 }}
					transition={{ duration: 0.3, ease: "easeInOut" }}
					className="overflow-hidden"
				>
					<div
						className={`pb-4 text-neutral-600 dark:text-neutral-400 ${className}`}
					>
						{children}
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	)
}
